import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditorModule } from 'primeng/editor';
import { ViewHtmlButtonDirective } from '../../directives/view-html-button.directive';
import { EmojiPickerButtonComponent } from '../emoji-picker-button/emoji-picker-button.component';

// QUILL — register the inline "tags" blot used by the dynamic-data dropdown.
import Quill from 'quill';
import Inline from 'quill/blots/inline';

class SignatureTagsBlot extends Inline {}
SignatureTagsBlot.blotName = 'tags';
SignatureTagsBlot.tagName = 'span';
Quill.register(SignatureTagsBlot);
// QUILL-END

/**
 * Bilingual (fr-CA / en-CA) rich-text signature editor with a "dynamic data"
 * dropdown that inserts ||TAG|| placeholders resolved server-side at send time.
 * Shared by the Organization and Organization Identity edit pages.
 */
@Component({
  selector: 'app-signature-rich-editor',
  templateUrl: './signature-rich-editor.component.html',
  styleUrls: ['./signature-rich-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EditorModule,
    ViewHtmlButtonDirective,
    TranslateModule,
    EmojiPickerButtonComponent,
  ],
})
export class SignatureRichEditorComponent implements OnInit {
  /** French signature (HTML). */
  @Input() signatureFr: string | null = '';
  @Output() signatureFrChange = new EventEmitter<string | null>();

  /** English signature (HTML). */
  @Input() signatureEn: string | null = '';
  @Output() signatureEnChange = new EventEmitter<string | null>();

  /** When false the signatures are shown rendered (read-only). */
  @Input() editMode: boolean = false;

  /**
   * Quill format whitelist = the toolbar buttons only. Pasting from Word or a
   * web page can no longer bring base64 images (blocked by Gmail/Outlook) or
   * styles the email cannot render.
   */
  public readonly allowedFormats: string[] = [
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'list',
    'indent',
    'link',
    'tags',
  ];

  private quillFR: any;
  private quillEN: any;
  public customTags: { label: string; value: string }[] = [];

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.customTags = [
      {
        label: this.translate.instant('GENERIC.TAG_FIRST_NAME_SENDER'),
        value: '||FIRST_NAME_SENDER||',
      },
      {
        label: this.translate.instant('GENERIC.TAG_LAST_NAME_SENDER'),
        value: '||LAST_NAME_SENDER||',
      },
      {
        label: this.translate.instant('GENERIC.TAG_FULL_NAME_SENDER'),
        value: '||FULL_NAME_SENDER||',
      },
      {
        label: this.translate.instant('GENERIC.TAG_ORG_NAME'),
        value: '||ORG_NAME||',
      },
      {
        label: this.translate.instant('GENERIC.TAG_ORG_TEL'),
        value: '||ORG_TEL||',
      },
    ];
    if (this.quillFR) {
      this.populateTagsDropdown(this.quillFR);
    }
    if (this.quillEN) {
      this.populateTagsDropdown(this.quillEN);
    }
  }

  public onEditorInitFR(event: any): void {
    this.quillFR = event.editor;
    this.setupCustomToolbar(this.quillFR);
    this.populateTagsDropdown(this.quillFR);
  }

  public onEditorInitEN(event: any): void {
    this.quillEN = event.editor;
    this.setupCustomToolbar(this.quillEN);
    this.populateTagsDropdown(this.quillEN);
  }

  private setupCustomToolbar(quillInstance: any): void {
    const toolbar = quillInstance.getModule('toolbar').container;
    const pickerOptions = toolbar.querySelector('.ql-tags .ql-picker-options');
    if (pickerOptions) {
      pickerOptions.addEventListener('mousedown', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains('ql-picker-item')) {
          const value = target.getAttribute('data-value');
          if (value) {
            const range = quillInstance.getSelection(true);
            quillInstance.insertText(range.index, value, 'user');
          }
        }
      });
    }
  }

  private populateTagsDropdown(quillInstance: any): void {
    if (!quillInstance || this.customTags.length === 0) {
      return;
    }
    const toolbar = quillInstance.getModule('toolbar').container;
    const pickerOptions = toolbar.querySelector('.ql-tags .ql-picker-options');
    const pickerLabel = toolbar.querySelector('.ql-tags .ql-picker-label');
    if (pickerOptions && pickerLabel) {
      pickerOptions.innerHTML = '';
      this.customTags.forEach((tag) => {
        const item = document.createElement('span');
        item.classList.add('ql-picker-item');
        item.dataset['value'] = tag.value;
        item.textContent = tag.label;
        pickerOptions.appendChild(item);
      });
      pickerLabel.textContent = this.translate.instant(
        'GENERIC.TAG_SELECT_LABEL',
      );
    }
  }

  public onSignatureFrChange(value: string | null): void {
    this.signatureFr = value;
    this.signatureFrChange.emit(value);
  }

  public onSignatureEnChange(value: string | null): void {
    this.signatureEn = value;
    this.signatureEnChange.emit(value);
  }
}
